import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '@/lib/axios';
import type { User } from '@/types/api.types';

interface LoginResponse {
  accessToken: string;
  user: User;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setTokens: (accessToken: string, user: User) => void;
  restoreSession: () => Promise<void>;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const { data } = await apiClient.post<LoginResponse>('/api/v1/auth/login', {
            email,
            password,
          });
          set({
            user: data.user,
            accessToken: data.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await apiClient.post('/api/v1/auth/logout');
        } catch {
          // ignore
        }
        get().clearAuth();
      },

      setTokens: (accessToken, user) => {
        set({ accessToken, user, isAuthenticated: true });
      },

      restoreSession: async () => {
        const { accessToken } = get();
        if (!accessToken) return;
        try {
          const { data } = await apiClient.get<User>('/api/v1/auth/me');
          set({ user: data, isAuthenticated: true });
        } catch {
          get().clearAuth();
        }
      },

      clearAuth: () => {
        set({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
      },
    }),
    {
      name: 'elbahi-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
